"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="223849" className="flex gap-8">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-4 w-20 bg-muted/20 animate-pulse" />)}
      </div>;
  }
  return <NavigationMenu>
      <NavigationMenuList className="gap-4">
        {menuItems.map((item, i) => {
        if (i === 4) return null;
        return <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-default text-xs font-bold uppercase tracking-widest text-foreground hover:text-primary transition-colors duration-300 whitespace-nowrap">
                {item.label === "The Sound" ? <>
                    <span className="text-foreground">The </span>
                    <span className="text-primary">Sound</span>
                  </> : item.label?.toUpperCase() === "LIVE EXPERIENCE" ? <>
                    <span className="text-primary">LIVE</span> <span className="text-foreground">EXPERIENCE</span>
                  </> : <span className="text-foreground">{item.label}</span>}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-transparent hover:opacity-70">
          <Menu className="size-5" />
          <span className="sr-only">Toggle Menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[85vw] sm:w-[350px] bg-background border-r border-border p-8">
        <div className="flex flex-col h-full">
          <div className="mb-12">
            <Link to="/" onClick={() => setIsOpen(false)}>
              
            </Link>
          </div>
          
          <nav className="flex flex-col gap-6 flex-1">
            {loading ? <div className="flex flex-col gap-6">
                {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-6 w-32 bg-muted/20 animate-pulse" />)}
              </div> : menuItems.map((item, i) => {
            if (i === 4) return null;
            return <div key={item.id} data-index={i}>
                  <Link to={item.href || "#"} className="block font-default text-lg font-bold uppercase tracking-widest text-foreground hover:text-primary transition-colors duration-300 whitespace-nowrap" onClick={() => setIsOpen(false)}>
                    {item.label === "The Sound" ? <>
                        <span className="text-foreground">The </span>
                        <span className="text-primary">Sound</span>
                      </> : item.label?.toUpperCase() === "LIVE EXPERIENCE" ? <>
                        <span className="text-primary">LIVE</span> <span className="text-foreground">EXPERIENCE</span>
                      </> : <span className="text-foreground">{item.label}</span>}
                  </Link>
                </div>;
          })}
          </nav>

          <div className="mt-auto pt-8 border-t border-border">
            <Button asChild variant="default" className="w-full bg-primary text-primary-foreground font-default uppercase tracking-widest text-xs h-12 hover:opacity-90 transition-all duration-300">
              <Link to="#">
                Book Session
              </Link>
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hashValue = '';
        if (href.startsWith('#')) {
          hashValue = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const parts = href.split('#');
          const path = parts[0];
          const hashPart = parts[1];
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hashValue = '#' + hashPart;
          }
        }
        if (hashValue && hashValue !== '#') {
          const element = document.querySelector(hashValue);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick as EventListener);
    return () => document.removeEventListener('click', handleAnchorClick as EventListener);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-border shadow-sm" : "bg-background border-b border-transparent"}`}>
      <div className="container mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between">
        
        <div className="flex-1 md:hidden flex justify-start">
          <MenuProvider menu_id="44">
            <MobileMenu />
          </MenuProvider>
        </div>

        <div className="flex-1 flex justify-center md:justify-start">
          <Link to="/" className="inline-block transition-opacity hover:opacity-80">
            <WvcLogo className="h-7 w-auto" />
          </Link>
        </div>

        <div className="hidden md:flex flex-auto justify-center">
          <MenuProvider menu_id="44">
            <DesktopMenu />
          </MenuProvider>
        </div>

        <div className="flex-1 flex justify-end">
          <Button asChild variant="default" className="hidden sm:inline-flex bg-primary text-primary-foreground font-default uppercase tracking-widest text-xs px-6 h-10 hover:opacity-90 transition-all duration-300">
            <Link to="/">
              Home
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}